import {
    AccountOwnership,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    NEW_APEX_IM_ACCOUNT_OPTION,
    NEW_CASH_ACCOUNT_OPTION,
} from '@fruitful/types';
import { type RefinementCtx, z } from 'zod';

import {
    accountSettingsFormSchema,
    type BaseAccountSettingsFormSchema,
    transferAccountSettingsFormSchema,
} from '../components/forms/AccountSettingsFormModule';
import {
    validateAccountNameLength,
    validateIndividualAccountOwnership,
    validatePhysicalCardLimits,
    validateRequiredFields,
    validateTransferAccountRowForMoneyMapRuleForm,
    validateTransferCashAccountFieldsWhenNotBackdoorRoth,
} from './moneyMapAccountFormUtils';

export const otherAccountsFormSchemaBase = z.object({
    ...accountSettingsFormSchema.shape,
    ...transferAccountSettingsFormSchema.shape,
    isBackdoorRoth: z.boolean().optional(),
});

type OtherAccountsFormShape = z.infer<typeof otherAccountsFormSchemaBase>;

function validatePrimaryCashOrApexAccount(
    accountSettings: BaseAccountSettingsFormSchema,
    account: MoneyMapAccountWithAccountDetails | undefined,
    ctx: RefinementCtx,
): boolean {
    const isNewCashAccount =
        accountSettings.moneyMapAccountId === NEW_CASH_ACCOUNT_OPTION.value;
    const isNewApexAccount =
        accountSettings.moneyMapAccountId === NEW_APEX_IM_ACCOUNT_OPTION.value;
    const isProposedCashAccount =
        account?.status === MoneyMapAccountStatus.PROPOSED &&
        account.type === MoneyMapAccountType.INTERNAL_BANKING;
    const isProposedApexAccount =
        account?.status === MoneyMapAccountStatus.PROPOSED &&
        account.type === MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT;

    const isNewOrProposedCashAccountValid =
        isNewCashAccount || isProposedCashAccount
            ? validateRequiredFields(
                  'account',
                  [
                      ['name', accountSettings.name],
                      ['ownership', accountSettings.ownership],
                      ['category', accountSettings.category],
                      ['virtualCard', accountSettings.virtualCard],
                      ['physicalCard', accountSettings.physicalCard],
                  ],
                  ctx,
              ) &&
              validateAccountNameLength('account', ctx, accountSettings.name)
            : true;

    const isNewOrProposedApexAccountValid =
        isNewApexAccount || isProposedApexAccount
            ? validateRequiredFields(
                  'account',
                  [
                      ['name', accountSettings.name],
                      ['ownership', accountSettings.ownership],
                      [
                          'apexInvestmentAccountType',
                          accountSettings.apexInvestmentAccountType,
                      ],
                      ['apexPortfolio', accountSettings.apexPortfolio],
                      ['apexRiskLevel', accountSettings.apexRiskLevel],
                      ['apexTimeHorizon', accountSettings.apexTimeHorizon],
                  ],
                  ctx,
              )
            : true;

    return isNewOrProposedCashAccountValid && isNewOrProposedApexAccountValid;
}

export function createOtherAccountsFormSuperRefine(
    accounts: MoneyMapAccountWithAccountDetails[],
    isSoloMembership: boolean,
) {
    return (
        {
            accountSettings,
            transferAccountSettings,
            isBackdoorRoth,
        }: OtherAccountsFormShape,
        ctx: RefinementCtx,
    ): boolean => {
        const isBackdoorRothPair = isBackdoorRoth === true;
        const account = accounts.find(
            (candidate) => candidate.id === accountSettings.moneyMapAccountId,
        );
        const transferAccount = accounts.find(
            (c) => c.id === transferAccountSettings?.moneyMapAccountId,
        );

        const isTransferAccountValid =
            validateTransferAccountRowForMoneyMapRuleForm(ctx, {
                isBackdoorRothPair,
                isIncomeSplitTransferRequired: false,
                transferAccountSettings,
                accountSettings,
                transferAccount,
            });

        const isAccountPhysicalCardValid = validatePhysicalCardLimits(
            'account',
            accounts,
            isSoloMembership,
            accountSettings,
            account,
            ctx,
        );

        const isAccountOwnershipValid =
            accountSettings.ownership === AccountOwnership.INDIVIDUAL
                ? validateIndividualAccountOwnership(
                      'account',
                      accountSettings.primaryUserId,
                      ctx,
                  )
                : true;

        const primaryRowsValid = validatePrimaryCashOrApexAccount(
            accountSettings,
            account,
            ctx,
        );

        const transferCashValid =
            validateTransferCashAccountFieldsWhenNotBackdoorRoth(
                isBackdoorRothPair,
                transferAccountSettings,
                transferAccount,
                ctx,
            );

        return (
            isTransferAccountValid &&
            isAccountPhysicalCardValid &&
            isAccountOwnershipValid &&
            primaryRowsValid &&
            transferCashValid
        );
    };
}
